import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
// hooks
import useLocales from '../../hooks/useLocales';
import useWhatsNew, { linkPath, pickText } from '../../hooks/useWhatsNew';
// components
import Iconify from '../../components/Iconify';
// utils
import getHelpUrl from '../../utils/helpUrl';

// ----------------------------------------------------------------------

WhatsNewDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  passedData: PropTypes.object,
};

// The release notes window. Any way of closing it marks the latest version as seen.
export default function WhatsNewDialog({ open, handleClose, passedData }) {
  const { translate, currentLang } = useLocales();
  const navigate = useNavigate();
  const { releases, markSeen } = useWhatsNew();
  const lang = currentLang.value;

  const [version, setVersion] = useState(passedData?.version || releases[0]?.version);
  const [imageOpen, setImageOpen] = useState(null);

  useEffect(() => {
    if (!version && releases[0]) setVersion(releases[0].version);
  }, [version, releases]);

  const release = releases.find((item) => item.version === version) || releases[0];

  const close = () => {
    markSeen();
    handleClose();
  };

  const openLink = (link) => {
    if (link.help) {
      window.open(getHelpUrl(lang), '_blank', 'noopener');
      return;
    }
    close();
    navigate(linkPath(link));
  };

  if (!release) return null;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Chip label={translate('whatsNew')} color="primary" size="small" variant="outlined" />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {`v${release.version}`}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton aria-label={translate('close')} onClick={close} size="small">
            <Iconify icon="eva:close-fill" width={20} height={20} />
          </IconButton>
        </Stack>
        <Typography variant="h6">{translate('whatsNewTitle')}</Typography>

        {releases.length > 1 && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
            {releases.map((item) => (
              <Chip
                key={item.version}
                label={`v${item.version}`}
                size="small"
                clickable
                color={item.version === release.version ? 'primary' : 'default'}
                variant={item.version === release.version ? 'filled' : 'outlined'}
                onClick={() => {
                  setVersion(item.version);
                  setImageOpen(null);
                }}
              />
            ))}
          </Stack>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack divider={<Divider flexItem />} spacing={2}>
          {release.items.map((item, index) => {
            const key = `${release.version}-${index}`;
            return (
              <Stack key={key} direction="row" spacing={2}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: 'primary.lighter',
                    color: 'primary.darker',
                    typography: 'caption',
                    fontWeight: 'fontWeightBold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {index + 1}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2">{pickText(item.title, lang)}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {pickText(item.text, lang)}
                  </Typography>

                  {item.where && (
                    <Typography variant="caption" component="p" sx={{ color: 'text.disabled', mt: 0.75 }}>
                      {`${translate('whatsNewWhere')}: ${pickText(item.where, lang)}`}
                    </Typography>
                  )}

                  {(item.link || item.image) && (
                    <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
                      {item.link && (
                        <Link component="button" variant="caption" underline="hover" onClick={() => openLink(item.link)}>
                          {`${translate(item.link.help ? 'whatsNewOpenHelp' : 'whatsNewOpenScreen')} →`}
                        </Link>
                      )}
                      {item.image && (
                        <Link
                          component="button"
                          variant="caption"
                          underline="hover"
                          onClick={() => setImageOpen(imageOpen === key ? null : key)}
                        >
                          {translate('whatsNewViewImage')}
                        </Link>
                      )}
                    </Stack>
                  )}

                  {item.image && imageOpen === key && (
                    <Box
                      component="img"
                      src={item.image}
                      alt={pickText(item.title, lang)}
                      sx={{ mt: 1, width: 1, borderRadius: 1, border: (theme) => `solid 1px ${theme.palette.divider}` }}
                    />
                  )}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={close}>
          {translate('whatsNewGotIt')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
